// The event-day page (spec §34; ADR 0018): one phone-friendly view of a Hub
// event on the day, refreshed every minute. It is only a view: ticket
// scanning, POS and live control keep working without it, and without Hub.
//
// Each section is shown only to someone who may see it. Incidents are a
// count and a link, only for incidents.read or incidents.manage, never a
// narrative (ADR 0015). Attendance and sales come from the same source
// reads as Event 360 (ADR 0010), with their freshness; a source that is
// missing or unavailable says so and is never shown as zero. No weather.

import { z } from 'zod'
import type { Event360 } from './event-360'

const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()

export const EVENT_DAY_REFRESH_MS = 60_000

export type RunOfShowItem = {
  id: string
  day: string
  timeLocal: string | null
  title: string
  ownerPersonId: string | null
  ownerName: string | null
  notes: string | null
  position: number
  doneAt: string | null
  doneByName: string | null
  version: number
}

export type EventDayShift = {
  id: string
  role: string
  departmentName: string | null
  startsLocal: string
  endsLocal: string
  status: string
  needed: number
  filled: number
  checkedIn: number
  checkedOut: number
  noShows: number
}

type Hidden = { hidden: true; reason: string }

export type EventDay = {
  event: { id: string; name: string; startsOn: string; endsOn: string; status: string }
  day: string
  days: string[]
  places: { facilityId: string; facilityName: string; spaceId: string | null; spaceName: string | null }[]
  runOfShow: RunOfShowItem[]
  // Counts per shift: no names.
  staffing: { shifts: EventDayShift[]; needed: number; filled: number; checkedIn: number; noShows: number }
  tasks: { items: { id: string; title: string; dueOn: string | null; status: string; assigneeName: string | null }[] } | Hidden
  workOrders: { items: { id: string; label: string; title: string; status: string; dueOn: string | null; assigneeName: string | null }[]; scope: 'all' | 'mine' }
  cases: { items: { id: string; number: number; subject: string; status: string; priority: string }[] } | Hidden
  // Count only, and only for incidents.read or incidents.manage.
  incidents: { total: number; open: number } | Hidden
  can: { manageRunOfShow: boolean; checkIn: boolean; reportIncident: boolean; openWorkOrder: boolean; seeShifts: boolean }
  asOf: string
}

// What the web Worker adds: the Event 360 source sections (BTownTix scans
// and sales, POS sales), with their freshness. Hidden without events.read.
export type EventDayView = EventDay & { sources: Pick<Event360, 'sources' | 'ticketing' | 'pos' | 'moneyVisible'> | Hidden }

export const RunOfShowBody = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  timeLocal: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
  title: text(200).min(1),
  ownerPersonId: ID.nullable().optional(),
  ownerLabel: optionalText(120),
  notes: optionalText(2000),
})
export const RunOfShowPatch = z.object({
  timeLocal: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).nullable().optional(),
  title: text(200).min(1).optional(),
  ownerPersonId: ID.nullable().optional(),
  ownerLabel: optionalText(120),
  notes: optionalText(2000),
  done: z.boolean().optional(),
  expectedVersion: z.number().int().min(1),
})
